<template>
  <slot v-if="initialLoadingError" name="initialLoadingError"></slot>
  <div ref="formRootEl">
    <form type="form">
      <ProductListLayout
        :show-filter="true"
        :results-text="resultsText"
        :data-testid-productlist-total="totalCount"
      >
        <template #filter>
          <ProductFilters
            :show-ina-link="showInALink"
            :lens-selector-in-modal="lensSelectorInModal"
          />
        </template>
        <template #actions>
          <ProductListActions
            v-if="productFilterStore.sortOptions.length > 0"
            :placeholder-label="
              $textDictionary['pdp.filters.sortPlaceholder.label'] ?? ''
            "
          />
        </template>
        <template #filterTags>
          <div v-if="lensSelectorInModal" class="mt-4 row">
            <div class="col px-2">
              <LensSelectorInModal
                :tool="lensSelectorInModal"
                :ga-event-category="'in_list'"
                :ls-link-type="'list'"
                @focal-length="
                  (minValue, maxValue) => setFocalLength(minValue, maxValue)
                "
              />
            </div>
          </div>
          <ProductFilterTags />
        </template>
        <template #list>
          <div v-if="productFilterStore.error">
            {{
              $textDictionary['productsList.error.message'] ??
              'An error occurred while loading the products [productsList.error.message].'
            }}
          </div>
          <div
            v-else-if="
              productsList.list.length === 0 && !productFilterStore.loading
            "
          >
            {{
              $textDictionary['productsList.noResultsHint'] ??
              'No Results for your Filter [productsList.noResultsHint].'
            }}
          </div>
          <ProductList
            :items="
              productFilterStore.loading ? loadingList : productsList.list
            "
            size="three-quarters"
            :display-mode="displayMode"
            :default-columns="defaultColumns"
            :attribute-columns="productsList.attributeColumns"
          >
            <template #card-footer="productCardProps">
              <template v-if="productFilterStore.loading">
                <LoadingSkeleton type="button" />
              </template>
              <ProductCardFooter
                v-else-if="
                  !productFilterStore.loading && displayMode === 'grid'
                "
                :has-fast-delivery="
                  productIdsByDelivery[productCardProps.product.id]
                "
                :product-card-props="productCardProps"
              />
            </template>
          </ProductList>
          <ProductListPagination :total-pages="productFilterStore.totalPages" />
        </template>
      </ProductListLayout>
    </form>
  </div>
</template>

<script setup lang="ts">
import {
  LoadingSkeleton,
  ProductList,
  ProductListLayout
} from '@hypercodestudio/basler-components';
import type { ProductCardProps } from '@hypercodestudio/basler-components/dist/components/modules/ProductCard.vue';
import ProductCardFooter from './ProductCardFooter.vue';
import ProductFilters from './ProductFilters.vue';
import ProductFilterTags from './ProductFilterTags.vue';
import ProductListPagination from './ProductListPagination.vue';
import ProductListActions from './ProductListActions.vue';
import { isDefined } from '~/utils/guards/isDefined';
import { useCreateProductDetailLink } from '~/composables/useCreateProductDetailLink';
import { useProductFilterStore } from '~/stores/productFilterStore/productFilterStore';
import { cleanProductFilters } from '~/stores/productFilterStore/helper/cleanProductFilters';
import { isEmpty } from '~/utils/isEmpty';
import type { ProductFiltersModel } from '~/stores/productFilterStore/model';
import { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import LensSelectorInModal from '~/components/lib/Tools/LensSelectorInModal.vue';

import type {
  IProductList,
  ITechnicalComponent
} from '~/lib/ContentfulService';
import type { AttributeColumn } from '@hypercodestudio/basler-components/dist/components/modules/ProductListTable.vue';

interface Props {
  parentSectionId: string;
  locale: string;
  store: string;
  // base filters, not visible in url or ui
  staticFilters?: ProductFiltersModel;
  // initial filters, visible in url and ui
  initialFilters?: ProductFiltersModel;
  type: 'Specifications' | 'Discontinued' | 'Plain';
  view: 'grid' | 'table-list';
  discontinuedSpecs: string[];
  showInALink: boolean;
  lensSelectorInModal?: ITechnicalComponent;
  isFullSizeImage: boolean;
  item: IProductList;
}

const props = withDefaults(defineProps<Props>(), {
  staticFilters: () => ({}),
  initialFilters: () => ({}),
  lensSelectorInModal: undefined
});

const route = useRoute();
const { $textDictionary, $globalPageSettings } = useNuxtApp();
const pdpConfiguration = usePdpConfiguration();
const productFilterStore = useProductFilterStore();

productFilterStore.$patch(() => {
  // XXX: should initial filters overwrite query parameters?
  if (isEmpty(route.query) && !isEmpty(props.initialFilters)) {
    productFilterStore.filters = cleanProductFilters(props.initialFilters);
  }
  productFilterStore.staticFilters = cleanProductFilters(props.staticFilters);
  productFilterStore.parentSectionId = props.parentSectionId;
  // reset the last response to keep the logic inside the route.query watcher
  // working for the first request.
  // @see https://gcp.baslerweb.com/jira/browse/DBP-329
  productFilterStore.lastResponse = null;
});

function setFocalLength(minValue: string, maxValue: string) {
  productFilterStore.handleFormValueChange('min_focallength_range', [
    minValue,
    maxValue
  ]);
}

onUnmounted(() => {
  // we reset the state on unmount to proper load a product list again,
  // e.g. while back/forward navigation on the previous page with a list.
  // @see https://gcp.baslerweb.com/jira/browse/DBP-1236
  productFilterStore.$reset();
});

watch(
  () => [route.query, productFilterStore.currentPage],
  async () => {
    // if we have pre-set filters we do not want to update the url
    // therefore, we apply the initial filters only on the first request.
    // XXX: This does not allow the user to navigate back to the pre-set filter

    if (
      productFilterStore.lastResponse == null &&
      isEmpty(route.query) &&
      !isEmpty(props.initialFilters)
    ) {
      await productFilterStore.getProducts();
    } else {
      await productFilterStore.createFiltersFromRouteAndUpdate(route.query);
    }

    // Fix anchor scrolling after loading products
    // @see https://gcp.baslerweb.com/jira/browse/DBP-636
    await fixAnchorScrollOnce();
  },
  { immediate: true }
);

const initialLoaded = computed(
  () => isDefined(productFilterStore.lastResponse) && !productFilterStore.error
);
const initialLoadingError = computed(
  () => !initialLoaded.value && productFilterStore.error
);

const totalCount = computed(() => {
  return productFilterStore.totalResultCount;
});

const resultsText = computed(() => {
  const currentPage = productFilterStore.currentPage;
  const pageSize = productFilterStore.pageSize;
  const totalCount = productFilterStore.totalResultCount;

  if (!totalCount || !pageSize) {
    return '';
  }

  const from = String((currentPage - 1) * pageSize + 1);
  const to = String(
    currentPage * pageSize <= totalCount ? currentPage * pageSize : totalCount
  );
  const total = String(totalCount);

  return (
    $textDictionary.value['productsList.resultsHint']
      ?.replace('{from}', from)
      .replace('{to}', to)
      .replace('{total}', total) ?? `${from}-${to} / ${total}`
  );
});
const createProductDetailLink = useCreateProductDetailLink();

const loadingList = computed((): ProductCardProps[] => {
  const product = {
    product: {
      id: '',
      name: 'Lorem ipsum',
      variantName: 'Lorem ipsum',
      description: 'Lorem ipsum',
      // XXX: wird nirgendwo angezeigt?
      price: -1,
      priceText: '',
      pricePrefix: '',
      attributes: [
        {
          key: 'lorem_ipsum',
          label: 'lorem_ipsum',
          value: 'lorem_ipsum'
        },
        {
          key: 'lorem_ipsum',
          label: 'lorem_ipsum',
          value: 'lorem_ipsum'
        },
        {
          key: 'lorem_ipsum',
          label: 'lorem_ipsum',
          value: 'lorem_ipsum'
        }
      ],
      media: {}
    },
    isLoading: true,
    attributesLoadingLines: 5,
    loadingLines: 5,
    isFullsizeImage: props.isFullSizeImage ?? false
  };
  const skeletonProducts = [];
  for (let i = 0; i < 9; i++) {
    skeletonProducts.push(product);
  }
  return skeletonProducts;
});

type Attribute = { key: string; label: string; value: string };
const productsList = computed<{
  list: ProductCardProps[];
  attributeColumns: AttributeColumn[];
}>(() => {
  const configuration = pdpConfiguration.value;
  if (!configuration) {
    return { list: [], attributeColumns: [] };
  }

  // 1. get product list entries and collect during unqiue attribute columns
  const shouldAttributesMap: Record<string, Attribute> = {};
  const list = productFilterStore.products.map((product) => {
    const productCardProps = DecoratedProduct.build(
      product,
      $textDictionary.value,
      props.locale
    ).asListEntry(
      configuration,
      productFilterStore.filters,
      createProductDetailLink,
      props.type,
      props.discontinuedSpecs,
      $globalPageSettings.value?.defaultProductImage ?? undefined
    );
    // collect unique attribute columns
    productCardProps.product.attributes.forEach((attribute: Attribute) => {
      shouldAttributesMap[attribute.key] = attribute;
    });

    productCardProps.isFullsizeImage = props.isFullSizeImage ?? false;

    return productCardProps;
  });

  if (displayMode.value === 'grid') {
    return { list, attributeColumns: [] };
  }

  // 2. get unique attribute columns
  const attributeColumns = Object.values(shouldAttributesMap).map(
    (attribute) => {
      return {
        key: attribute.key,
        columnLabel: attribute.label
      };
    }
  );

  if (props.discontinuedSpecs) {
    attributeColumns.sort((a, b) => {
      return (
        props.discontinuedSpecs.indexOf(a.key) -
        props.discontinuedSpecs.indexOf(b.key)
      );
    });
  }

  const hasDescription = list.some((card) => card.product.description !== ' ');

  // 3. iterate list entries and polyfill / order attributes (necessary by hypercode component)
  list.forEach((p) => {
    const hasAttributeKeys = p.product.attributes.map(
      (attribute: Attribute) => attribute.key
    );
    const orderedAttributes: Attribute[] = [];
    attributeColumns.forEach((attributeColumn) => {
      const attributeKey = attributeColumn.key;
      if (!hasAttributeKeys.includes(attributeKey)) {
        orderedAttributes.push({
          key: attributeKey,
          label: shouldAttributesMap[attributeKey].label,
          value: ' '
        });
      } else {
        orderedAttributes.push(
          p.product.attributes.find(
            (attribute: Attribute) => attribute.key === attributeKey
          )
        );
      }
    });
    p.product.attributes = orderedAttributes;
    p.product.description = !hasDescription ? '' : p.product.description;
  });

  return {
    list: list,
    attributeColumns: attributeColumns ?? []
  };
});

const defaultColumns = computed(() => {
  const columns = [
    {
      key: 'media',
      columnLabel:
        $textDictionary.value['productsList.tableList.column.media'] ?? '',
      hideColumnLabel: true
    },
    {
      key: 'title',
      columnLabel:
        $textDictionary.value['productsList.tableList.column.title'] ?? ''
    }
  ];

  if (props.type === 'Specifications') {
    columns.push({
      key: 'priceText',
      columnLabel:
        $textDictionary.value['productsList.tableList.column.priceText'] ?? ''
    });
  }

  const hasDescription = productsList.value.list.some(
    (card) => card.product.description === ''
  );
  if (!hasDescription) {
    columns.push({
      key: 'description',
      columnLabel:
        $textDictionary.value['productsList.tableList.column.description'] ??
        '',
      hideColumnLabel: true
    });
  }

  return columns;
});

const displayMode = computed((): IProductList['fields']['view'] => {
  if (!productFilterStore.view) {
    productFilterStore.changeView(props.view);
    return props.view;
  }
  return productFilterStore.view;
});

type ProductByDelivery = Record<string, boolean>;

const productIdsByDelivery = computed<ProductByDelivery>(() => {
  return productFilterStore.products.reduce((prev, product) => {
    const decoratedProduct = DecoratedProduct.build(product);
    return { ...prev, [decoratedProduct.id]: decoratedProduct.hasFastDelivery };
  }, {});
});
</script>

<style>
.product-list-layout__meta-bar-actions {
  max-height: 24px;
}

.product-list-layout__filter-tags {
  min-height: 39px;
}
</style>
